import { useEffect } from "react"
import { skipToken, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { PREVIEW_FRESHNESS, type RunFeedPreviewFreshness } from "@nova-ai/shared"

import { queryKeys } from "./query-keys"

type RecoveryObservation = {
  restrictedAt: number
  restricted: RunFeedPreviewFreshness | undefined
  needsValidation: boolean
}

const EMPTY_RECOVERY: RecoveryObservation = {
  restrictedAt: 0,
  restricted: undefined,
  needsValidation: false,
}

/** A cached variant cannot erase a newer noncurrent observation from another variant. */
export function previewFreshnessSinceRecovery(
  client: QueryClient,
  slug: string,
  freshness: RunFeedPreviewFreshness | undefined,
  updatedAt: number
) {
  const recovery = client.getQueryData<RecoveryObservation>(queryKeys.runPreviewRecovery(slug))
  return recovery?.restricted && updatedAt <= recovery.restrictedAt
    ? recovery.restricted
    : freshness
}

/** Keep observed recovery requirements for this QueryClient session, including route remounts. */
export function useRunPreviewRecovery(
  slug: string,
  preview: { freshness: RunFeedPreviewFreshness } | undefined,
  error: Error | null,
  updatedAt: number
) {
  const client = useQueryClient()
  const { data: recovery = EMPTY_RECOVERY } = useQuery<RecoveryObservation>({
    queryKey: queryKeys.runPreviewRecovery(slug),
    queryFn: skipToken,
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: EMPTY_RECOVERY,
  })
  const observedFreshness = preview?.freshness
  const isRestricted =
    observedFreshness !== undefined && observedFreshness.kind !== PREVIEW_FRESHNESS.current
  const freshness = previewFreshnessSinceRecovery(client, slug, observedFreshness, updatedAt)
  const needsValidation = isRestricted || recovery.needsValidation
  const restrictedAt = isRestricted
    ? Math.max(updatedAt, recovery.restrictedAt)
    : recovery.restrictedAt

  useEffect(() => {
    if (isRestricted && updatedAt > recovery.restrictedAt) {
      client.setQueryData<RecoveryObservation>(queryKeys.runPreviewRecovery(slug), (current) =>
        current && current.restrictedAt >= updatedAt
          ? current
          : { restrictedAt: updatedAt, restricted: observedFreshness, needsValidation: true }
      )
    }
    if (restrictedAt) {
      void client.invalidateQueries({
        queryKey: queryKeys.runFeedPreview(slug),
        // The noncurrent response itself is already fresh. Invalidate older variants only.
        predicate: (query) =>
          query.state.dataUpdatedAt < restrictedAt && !query.state.isInvalidated,
      })
    }
  }, [
    client,
    slug,
    isRestricted,
    observedFreshness,
    updatedAt,
    restrictedAt,
    recovery.restrictedAt,
  ])

  useEffect(() => {
    if (freshness?.kind !== PREVIEW_FRESHNESS.current || !needsValidation) {
      return
    }
    void client.invalidateQueries({ queryKey: queryKeys.runStatus(slug) })
    void client.invalidateQueries({ queryKey: queryKeys.runDetail(slug) })
    void client.invalidateQueries({ queryKey: queryKeys.runFacets(slug) })
  }, [client, slug, freshness?.kind, needsValidation])

  return {
    freshness,
    canValidate: freshness?.kind === PREVIEW_FRESHNESS.current && !error,
    needsValidation,
    onValidated: () =>
      client.setQueryData<RecoveryObservation>(queryKeys.runPreviewRecovery(slug), (current) =>
        current?.restrictedAt === restrictedAt ? { ...current, needsValidation: false } : current
      ),
  }
}
